import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  styled,
  tableCellClasses,
  useTheme,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { OrganizationCompletion } from "services/organizationPortalClient";

const useStyles = (_: Theme) => ({
  tableHead: {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.primary.main,
    fontSize: 12,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "5px 15px 5px 15px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "default",
  backgroundColor: theme.palette.background.default,
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

type CertificateDownloadRequest = {
  userId: string;
  userCertificateId: string;
};

export default function CertificatesExpandedInfo({
  value,
}: Readonly<{
  value: OrganizationCompletion;
}>) {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { certificatesService } = useGlobalContext();
  const [certificateDownloadRequest, setCertificateDownloadRequest] =
    useState<CertificateDownloadRequest | null>(null);

  const certificateDownloadUrlQuery = useQuery<string>(
    ["certificate-download", certificateDownloadRequest],
    () =>
      certificatesService.getDownloadUrl(
        certificateDownloadRequest?.userId!,
        certificateDownloadRequest?.userCertificateId!
      ),
    {
      enabled: !!certificateDownloadRequest,
    }
  );

  useEffect(() => {
    if (
      certificateDownloadUrlQuery.data &&
      !certificateDownloadUrlQuery.isLoading
    ) {
      window.open(certificateDownloadUrlQuery.data, "_blank");
    }
  }, [certificateDownloadUrlQuery.data, certificateDownloadUrlQuery.isLoading]);

  const getCertificateType = (
    professionName?: string,
    qualificationName?: string
  ) => {
    if (qualificationName) {
      return `${professionName} / ${qualificationName}`;
    }
    return professionName ?? "";
  };

  return (
    <Table
      role="table"
      aria-label={`${t({ id: "certificates" })} - ${value.courseTitle ?? ""}`}
      size="small"
    >
      <TableHead>
        <StyledTableRow sx={styles.tableHead}>
          {value.userCertificates?.length ? (
            <>
              <StyledTableCell>
                <T id="certificateType" />
              </StyledTableCell>
              <StyledTableCell align="center">
                <T id="credits" />
              </StyledTableCell>
              <StyledTableCell></StyledTableCell>
            </>
          ) : (
            <StyledTableCell>
              <T id="certificatesNotFound" />
            </StyledTableCell>
          )}
        </StyledTableRow>
      </TableHead>
      <TableBody>
        {value.userCertificates?.map((userCertificate) => (
          <StyledTableRow key={userCertificate.id}>
            <StyledTableCell width="40%">
              {getCertificateType(
                userCertificate.professionName,
                userCertificate.qualificationName
              )}
            </StyledTableCell>
            <StyledTableCell align="center">
              {userCertificate.credits.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell align="right">
              <Button
                type="submit"
                variant="contained"
                size="small"
                fullWidth={false}
                disabled={certificateDownloadUrlQuery.isLoading}
                onClick={() =>
                  setCertificateDownloadRequest({
                    userId: userCertificate.userId,
                    userCertificateId: userCertificate.id,
                  })
                }
              >
                <T id="viewCertificate" />
              </Button>
            </StyledTableCell>
          </StyledTableRow>
        ))}
      </TableBody>
    </Table>
  );
}
